// Sound settings — master volume + mute, persisted to localStorage,
// exposed as an external store React can subscribe to via
// useSyncExternalStore. The sfx player reads getSettings() directly
// (it's not a React consumer); UI surfaces use useSoundSettings().
//
// Version-key migration per CLAUDE.md: bump SOUND_VERSION_CURRENT when
// DEFAULT_VOLUME / DEFAULT_MUTED change so existing devices adopt the
// new default once, while still respecting explicit user picks made
// under the current version.

const VOLUME_KEY = 'origami-sfx-volume'
const MUTED_KEY = 'origami-sfx-muted'
const SHOP_BED_KEY = 'origami-shop-bed'
const BATTLE_BED_KEY = 'origami-battle-bed'
const VERSION_KEY = 'origami-sfx-version'
// Bump when a DEFAULT_* changes so existing devices adopt it once.
// v2: default music switched to the full composed themes.
const SOUND_VERSION_CURRENT = '2'

const DEFAULT_VOLUME = 0.7
const DEFAULT_MUTED = false
/** Default ambience track ids (see SHOP_BEDS / BATTLE_BEDS in
 *  ambience.ts). 'off' silences that bed. */
const DEFAULT_SHOP_BED = 'moonlit'
const DEFAULT_BATTLE_BED = 'moonkoto'

export interface SoundSettings {
  /** Master gain, 0..1. */
  volume: number
  muted: boolean
  /** Selected shop ambience track id, or 'off'. */
  shopBed: string
  /** Selected battle ambience track id, or 'off'. */
  battleBed: string
}

let state: SoundSettings = load()
const listeners = new Set<() => void>()

function load(): SoundSettings {
  if (typeof window === 'undefined') {
    return {
      volume: DEFAULT_VOLUME,
      muted: DEFAULT_MUTED,
      shopBed: DEFAULT_SHOP_BED,
      battleBed: DEFAULT_BATTLE_BED,
    }
  }
  const savedVersion = window.localStorage.getItem(VERSION_KEY)
  if (savedVersion !== SOUND_VERSION_CURRENT) {
    // First load under this version — adopt the new bed defaults once,
    // but PRESERVE the user's volume / mute if they already set them
    // (only those two are "personal"; bed defaults are content picks).
    const rawVol = window.localStorage.getItem(VOLUME_KEY)
    const v = rawVol === null ? DEFAULT_VOLUME : Number(rawVol)
    const vol = Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : DEFAULT_VOLUME
    const mut = window.localStorage.getItem(MUTED_KEY) === 'true'
    window.localStorage.setItem(VERSION_KEY, SOUND_VERSION_CURRENT)
    window.localStorage.setItem(VOLUME_KEY, String(vol))
    window.localStorage.setItem(MUTED_KEY, String(mut))
    window.localStorage.setItem(SHOP_BED_KEY, DEFAULT_SHOP_BED)
    window.localStorage.setItem(BATTLE_BED_KEY, DEFAULT_BATTLE_BED)
    return { volume: vol, muted: mut, shopBed: DEFAULT_SHOP_BED, battleBed: DEFAULT_BATTLE_BED }
  }
  const rawVol = window.localStorage.getItem(VOLUME_KEY)
  const vol = rawVol === null ? DEFAULT_VOLUME : Number(rawVol)
  return {
    volume: Number.isFinite(vol) ? Math.max(0, Math.min(1, vol)) : DEFAULT_VOLUME,
    muted: window.localStorage.getItem(MUTED_KEY) === 'true',
    // Additive fields — default when absent (existing v1 devices).
    shopBed: window.localStorage.getItem(SHOP_BED_KEY) ?? DEFAULT_SHOP_BED,
    battleBed: window.localStorage.getItem(BATTLE_BED_KEY) ?? DEFAULT_BATTLE_BED,
  }
}

function persist(): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(VOLUME_KEY, String(state.volume))
  window.localStorage.setItem(MUTED_KEY, String(state.muted))
  window.localStorage.setItem(SHOP_BED_KEY, state.shopBed)
  window.localStorage.setItem(BATTLE_BED_KEY, state.battleBed)
}

function emit(): void {
  for (const l of listeners) l()
}

export function getSoundSettings(): SoundSettings {
  return state
}

export function setVolume(volume: number): void {
  state = { ...state, volume: Math.max(0, Math.min(1, volume)) }
  persist()
  emit()
}

export function setMuted(muted: boolean): void {
  state = { ...state, muted }
  persist()
  emit()
}

export function toggleMuted(): void {
  setMuted(!state.muted)
}

export function setShopBed(shopBed: string): void {
  state = { ...state, shopBed }
  persist()
  emit()
}

export function setBattleBed(battleBed: string): void {
  state = { ...state, battleBed }
  persist()
  emit()
}

/** Effective gain a sound should play at — 0 while muted. */
export function masterGain(): number {
  return state.muted ? 0 : state.volume
}

export function subscribeSoundSettings(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
